import { type ColumnDef } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { Trophy, Medal } from "lucide-react";
import { OverviewStatsTable } from "@/components/general/Dashboard/Overview/OverviewStatsTable";
import { getKpiTier, getPointsBadge } from "@/lib/utils";

export type LeaderboardRow = {
    rank: number;
    playerId: number;
    firstName: string;
    lastName: string;
    tournamentName: string | null;
    matchesPlayed: number;
    value: number;
};

const RankCell = ({ rank, compact }: { rank: number; compact?: boolean }) => {
    const iconSize = compact ? "w-3.5 h-3.5" : "w-4 h-4";
    if (rank === 1) return <Trophy className={`${iconSize} text-yellow-500`} />;
    if (rank === 2) return <Medal className={`${iconSize} text-slate-400`} />;
    if (rank === 3) return <Medal className={`${iconSize} text-orange-400`} />;
    return (
        <span className={`font-headline font-bold ${compact ? "text-xs" : "text-sm"} text-on-surface-variant`}>
            #{rank.toString().padStart(2, "0")}
        </span>
    );
};

const buildColumns = (valueLabel: string, isKpi: boolean, compact = false): ColumnDef<LeaderboardRow>[] => [
    {
        id: "rank",
        header: "#",
        size: compact ? 40 : 72,
        cell: ({ row }) => <RankCell rank={row.original.rank} compact={compact} />,
    },
    {
        id: "player",
        header: "Player",
        size: compact ? 95 : 200,
        cell: ({ row }) => (
            <Link
                to="/player/$playerId"
                params={{ playerId: row.original.playerId.toString() }}
                className={`font-bold text-on-surface hover:text-primary hover:underline transition-colors ${compact ? "text-xs truncate block" : ""}`}
            >
                {row.original.firstName} {row.original.lastName}
            </Link>
        ),
    },
    {
        accessorKey: "matchesPlayed",
        header: "GP",
        size: compact ? 40 : 100,
        cell: ({ getValue }) => (
            <span className={`text-on-surface-variant ${compact ? "text-xs" : ""}`}>{getValue() as number}</span>
        ),
    },
    {
        accessorKey: "value",
        header: valueLabel,
        size: compact ? 65 : 120,
        cell: ({ getValue }) => {
            const v = getValue() as number;
            if (!isKpi) {
                if (compact) {
                    return (
                        <div className="inline-flex items-center px-2 py-0.5 border border-transparent rounded">
                            <span className="font-bold text-primary text-xs">{v}</span>
                        </div>
                    );
                }
                return <span className="font-bold text-primary text-lg">{v}</span>;
            }
            if (compact) {
                const { bg, text, border } = getPointsBadge(v);
                return (
                    <div
                        className={`inline-flex items-center px-2 py-0.5 ${bg} ${text} rounded font-bold text-xs border ${border}`}
                    >
                        {v.toFixed(1)}
                    </div>
                );
            }
            const tier = getKpiTier(v);
            return (
                <span className="font-bold text-lg" style={{ color: tier.color }}>
                    {v.toFixed(1)}
                </span>
            );
        },
    },
];

interface LeaderboardTableProps {
    data: LeaderboardRow[];
    isLoading: boolean;
    valueLabel: string;
    isKpi?: boolean;
    compact?: boolean;
}

export function LeaderboardTable({
    data,
    isLoading,
    valueLabel,
    isKpi = false,
    compact = false,
}: LeaderboardTableProps) {
    const columns = buildColumns(valueLabel, isKpi, compact);
    return (
        <OverviewStatsTable
            columns={columns as ColumnDef<Record<string, unknown>>[]}
            data={data as unknown as Record<string, unknown>[]}
            isLoading={isLoading}
            defaultSortId="value"
            compact={compact}
            noHorizontalScroll={compact}
        />
    );
}
